import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Loader2,
  Plus,
  Search,
  Signature,
  Tags,
  Users,
  Video,
} from 'lucide-react'
import { CourseAdminCard } from '../-components/CourseAdminCard'
import { CourseRequestsPanel } from '../-components/CourseRequestsPanel'
import { OnboardingPanel } from '../-components/OnboardingPanel'
import { VidsNeedingAttentionCard } from '../-components/VidsNeedingAttentionCard'
import type { LearningCourseSummary } from '@/types/learning.types'
import {
  useCourseRequests,
  useLearningCourses,
  useLearningOnboarding,
  useVidsNeedingAttention,
} from '@/hooks/repositories/useLearningRepository'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ProgressRing } from '@/components/shared/ProgressRing'
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { useCan } from '@/hooks/permissions/use-can'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'

export const Route = createFileRoute('/_app/learning/admin/')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
  component: RouteComponent,
})

type Filter = 'all' | 'attention' | 'published' | 'drafts' | 'recurring'

const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'all', label: 'All' },
  { value: 'published', label: 'Live' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'recurring', label: 'Recurring' },
]

function matches(course: LearningCourseSummary, filter: Filter): boolean {
  switch (filter) {
    case 'attention':
      return course.stats.overdue > 0 || course.stats.signaturesOutstanding > 0
    case 'published':
      return course.publishedVersion !== null
    case 'drafts':
      return course.draftVersion !== null
    case 'recurring':
      return course.recurrence !== null
    default:
      return true
  }
}

/**
 * The Learning admin overview (sc-1245).
 *
 * Opens on NEEDS ATTENTION rather than on everything. The reason to come here
 * is almost always that something is outstanding — the full catalogue is what
 * you ask for once that is dealt with, not what you read past first. It falls
 * back to All when nothing needs attention, so a healthy book never lands on an
 * empty tab.
 */
function RouteComponent() {
  const { has } = useCan()
  const { data: courses, isPending } = useLearningCourses()
  // The people-level view is `learning.reports`, a different and more sensitive
  // grant than authoring — an author without it must not fetch it at all.
  const canSeeReports = has(LEARNING_PERMISSION_KEYS.reports)
  const { data: onboarding } = useLearningOnboarding(canSeeReports)
  const { data: requests } = useCourseRequests(
    has(LEARNING_PERMISSION_KEYS.author),
  )

  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState<Filter>('attention')

  const totals = React.useMemo(() => {
    const list = courses ?? []
    const assigned = list.reduce((sum, c) => sum + c.stats.assigned, 0)
    const completed = list.reduce((sum, c) => sum + c.stats.completed, 0)
    return {
      assigned,
      completed,
      overdue: list.reduce((sum, c) => sum + c.stats.overdue, 0),
      unsigned: list.reduce((sum, c) => sum + c.stats.signaturesOutstanding, 0),
      percentComplete:
        assigned === 0 ? 0 : Math.round((completed / assigned) * 100),
      liveCourses: list.filter((c) => c.publishedVersion).length,
    }
  }, [courses])

  // Videos count too, or the tab reads "0" while holding something, and the
  // fallback below bounces you off a tab that had a reason to be open.
  const { data: staleVids = [] } = useVidsNeedingAttention()
  const attentionCount = React.useMemo(
    () =>
      (courses ?? []).filter((c) => matches(c, 'attention')).length +
      staleVids.length,
    [courses, staleVids.length],
  )

  // Fall back to All once the book is healthy, rather than opening on a tab
  // that is empty for a good reason.
  React.useEffect(() => {
    if (courses && attentionCount === 0 && filter === 'attention') {
      setFilter('all')
    }
  }, [courses, attentionCount, filter])

  const visible = React.useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (courses ?? [])
      .filter((course) => matches(course, filter))
      .filter((course) => {
        if (!needle) return true
        const title =
          course.publishedVersion?.title ?? course.draftVersion?.title ?? ''
        return (
          title.toLowerCase().includes(needle) ||
          course.slug.toLowerCase().includes(needle)
        )
      })
  }, [courses, filter, search])

  return (
    <div className="py-4">
      <PageTitle title="Learning — Admin" />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Learning
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Company-wide training — what is published, who has done it, and what
            is outstanding.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/learning/admin/media">
              <Video className="mr-1 size-4" aria-hidden />
              Videos
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/learning/admin/properties">
              <Tags className="mr-1 size-4" aria-hidden />
              Properties
            </Link>
          </Button>
          {canSeeReports && (
            <Button asChild size="sm" variant="outline">
              <Link to="/learning/admin/reports">
                <BarChart3 className="mr-1 size-4" aria-hidden />
                Reporting
              </Link>
            </Button>
          )}
          <Button asChild size="sm">
            <Link to="/learning/admin/new">
              <Plus className="mr-1 size-4" aria-hidden />
              New course
            </Link>
          </Button>
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* ── Company rollup ───────────────────────────────────────── */}
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="sm:col-span-2">
              <CardContent className="flex items-center gap-5 py-5">
                <ProgressRing percent={totals.percentComplete} size={72} />
                <div className="min-w-0">
                  <p className="text-xl font-bold tabular-nums sm:text-2xl">
                    {totals.completed}
                    <span className="text-base font-normal text-muted-foreground">
                      {' '}
                      / {totals.assigned}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    assignments completed across {totals.liveCourses}{' '}
                    {totals.liveCourses === 1 ? 'course' : 'courses'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card
              className={
                totals.overdue > 0 ? 'border-destructive/40' : undefined
              }
            >
              <CardContent className="py-5">
                <div
                  className={
                    totals.overdue > 0
                      ? 'mb-2 inline-flex rounded-lg bg-destructive/10 p-2 text-destructive'
                      : 'mb-2 inline-flex rounded-lg bg-muted p-2 text-muted-foreground'
                  }
                >
                  <AlertTriangle className="size-4" aria-hidden />
                </div>
                <p className="text-xl font-bold tabular-nums sm:text-2xl">
                  {totals.overdue}
                </p>
                <p className="text-sm text-muted-foreground">overdue</p>
              </CardContent>
            </Card>

            <Card
              className={
                totals.unsigned > 0 ? 'border-amber-500/40' : undefined
              }
            >
              <CardContent className="py-5">
                <div
                  className={
                    totals.unsigned > 0
                      ? 'mb-2 inline-flex rounded-lg bg-amber-500/10 p-2 text-amber-700 dark:text-amber-400'
                      : 'mb-2 inline-flex rounded-lg bg-muted p-2 text-muted-foreground'
                  }
                >
                  <Signature className="size-4" aria-hidden />
                </div>
                <p className="text-xl font-bold tabular-nums sm:text-2xl">
                  {totals.unsigned}
                </p>
                <p className="text-sm text-muted-foreground">
                  signatures outstanding
                </p>
              </CardContent>
            </Card>
          </div>

          {/* What people are asking for comes BEFORE what exists: it is the
              only thing on this page nobody else is looking at. */}
          {requests && <CourseRequestsPanel requests={requests} />}

          {/* ── New joiners ──────────────────────────────────────────── */}
          {canSeeReports && onboarding && <OnboardingPanel rows={onboarding} />}

          {/* ── Filters ──────────────────────────────────────────────── */}
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs
              value={filter}
              onValueChange={(value) => setFilter(value as Filter)}
            >
              <TabsList>
                {FILTERS.map((option) => (
                  <TabsTrigger key={option.value} value={option.value}>
                    {option.label}
                    {option.value === 'attention' && attentionCount > 0 && (
                      <span className="ml-1.5 rounded-full bg-destructive/15 px-1.5 text-[10px] font-medium text-destructive tabular-nums">
                        {attentionCount}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div className="relative lg:w-72">
              <Search
                className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by title or slug"
                className="pl-8"
                aria-label="Search courses"
              />
            </div>
          </div>

          {/* ── Courses ──────────────────────────────────────────────── */}
          {/* Under the tab that already means "needs attention", rather than
              floating above the page as a second, unrelated banner. Videos
              whose source has moved on are the same kind of problem as a course
              that is not ready — a thing an admin has to decide about. */}
          {filter === 'attention' && (
            <div className="mb-4 empty:mb-0">
              <VidsNeedingAttentionCard />
            </div>
          )}

          {visible.length === 0 &&
          !(filter === 'attention' && staleVids.length > 0) ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                <div className="rounded-full bg-muted p-3">
                  <BookOpen
                    className="size-6 text-muted-foreground"
                    aria-hidden
                  />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {courses?.length ? 'Nothing here' : 'No courses yet'}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {courses?.length
                      ? 'Try another filter or search.'
                      : 'Create a course to get started.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visible.map((course) => (
                <CourseAdminCard key={course.id} course={course} />
              ))}
            </div>
          )}

          {/* Deliberately after the grid: a count is a footnote once you can
              see the cards, not a heading. */}
          {visible.length > 0 && (
            <p className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="size-3" aria-hidden />
              {visible.length} of {courses?.length ?? 0} courses
            </p>
          )}
        </>
      )}
    </div>
  )
}
